import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { AppConfigConnected, saveAppConfig } from '../actions';
import GridHeader from '../../common/gridheader/GridHeader';
import Grid from '../../webcommon/grid/Grid';
import styles from './AppConfig.module.scss';
import { appConfigColumns } from '../constants';
import * as selectors from '../selectors';

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const AppConfig = (props) => {
    const {
        AppConfigConnected,
        saveAppConfig,
        appConfig,
        appConfigSaving,
        appConfigSaveError,
        appConfigSaved
    } = props;

    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);
    const successTimerRef = useRef(null);
    const prevSavedRef = useRef(false);

    useEffect(() => {
        AppConfigConnected();
        return () => {
            if (successTimerRef.current) clearTimeout(successTimerRef.current);
        };
    }, [AppConfigConnected]);

    useEffect(() => {
        if (appConfigSaved && !prevSavedRef.current) {
            setSuccessMessage('Saved — WebJobs restarting');
            successTimerRef.current = setTimeout(() => {
                setSuccessMessage(null);
                setEditing(false);
            }, 3000);
        }
        prevSavedRef.current = appConfigSaved;
    }, [appConfigSaved]);

    if (!appConfig) return null;

    const startEditing = () => {
        setDraft({
            dailyBatchStartTime:          appConfig.dailyBatchStartTime,
            dailyBatchFileWaitTimeoutMins: appConfig.dailyBatchFileWaitTimeoutMins,
            dailyBatchDefaultTimeoutMins:  appConfig.dailyBatchDefaultTimeoutMins,
            dailyBatchTimerIntervalMins:   appConfig.dailyBatchTimerIntervalMins,
            commodityReportEnabled:        appConfig.commodityReportEnabled,
            commodityReportStartTime:      appConfig.commodityReportStartTime,
            iceDownloadStartTime:          appConfig.iceDownloadStartTime,
            archivingStartTime:            appConfig.archivingStartTime,
            archivingDayOfWeek:            appConfig.archivingDayOfWeek
        });
        setEditing(true);
    };

    const cancelEditing = () => {
        if (successTimerRef.current) clearTimeout(successTimerRef.current);
        setEditing(false);
        setDraft({});
        setSuccessMessage(null);
    };

    const hasChanges =
        draft.dailyBatchStartTime          !== appConfig.dailyBatchStartTime          ||
        draft.dailyBatchFileWaitTimeoutMins !== appConfig.dailyBatchFileWaitTimeoutMins ||
        draft.dailyBatchDefaultTimeoutMins  !== appConfig.dailyBatchDefaultTimeoutMins  ||
        draft.dailyBatchTimerIntervalMins   !== appConfig.dailyBatchTimerIntervalMins   ||
        draft.commodityReportEnabled        !== appConfig.commodityReportEnabled        ||
        draft.commodityReportStartTime      !== appConfig.commodityReportStartTime      ||
        draft.iceDownloadStartTime          !== appConfig.iceDownloadStartTime          ||
        draft.archivingStartTime            !== appConfig.archivingStartTime            ||
        draft.archivingDayOfWeek            !== appConfig.archivingDayOfWeek;

    const handleSave = () => {
        saveAppConfig({
            dailyBatchStartTime:          draft.dailyBatchStartTime,
            dailyBatchFileWaitTimeoutMins: draft.dailyBatchFileWaitTimeoutMins,
            dailyBatchDefaultTimeoutMins:  draft.dailyBatchDefaultTimeoutMins,
            dailyBatchTimerIntervalMins:   draft.dailyBatchTimerIntervalMins,
            commodityReportEnabled:        draft.commodityReportEnabled,
            commodityReportStartTime:      draft.commodityReportStartTime,
            iceDownloadStartTime:          draft.iceDownloadStartTime,
            archivingStartTime:            draft.archivingStartTime,
            archivingDayOfWeek:            draft.archivingDayOfWeek
        });
    };

    const update = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

    const timeCell = (field, value) => editing
        ? <input type="time" value={draft[field] ?? ''} onChange={update(field)} />
        : value;

    const textCell = (field, value) => editing
        ? <input type="text" value={draft[field] ?? ''} onChange={update(field)} style={{ width: '80px' }} />
        : value;

    const dayCell = (field, value) => editing
        ? (
            <select value={draft[field] ?? ''} onChange={update(field)}>
                {DAYS_OF_WEEK.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
        )
        : value;

    const boolCell = (field, value) => {
        const isEnabled = (editing ? draft[field] : value) === 'true';
        return editing
            ? (
                <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => setDraft({ ...draft, [field]: e.target.checked ? 'true' : 'false' })}
                />
            )
            : (isEnabled ? 'Enabled' : 'Disabled');
    };

    const generalRows = [
        { label: 'Environment',       value: appConfig.environment },
        { label: 'SIMM Model Version', value: appConfig.simmModelVersion }
    ];

    const dailyBatchRows = [
        { label: 'Start Time (London)',         value: timeCell('dailyBatchStartTime', appConfig.dailyBatchStartTime) },
        { label: 'File Wait Timeout (mins)',     value: textCell('dailyBatchFileWaitTimeoutMins', appConfig.dailyBatchFileWaitTimeoutMins) },
        { label: 'Default Timeout (mins)',       value: textCell('dailyBatchDefaultTimeoutMins', appConfig.dailyBatchDefaultTimeoutMins) },
        { label: 'Timer Interval (mins)',        value: textCell('dailyBatchTimerIntervalMins', appConfig.dailyBatchTimerIntervalMins) }
    ];

    const commodityRows = [
        { label: 'Enabled',             value: boolCell('commodityReportEnabled', appConfig.commodityReportEnabled) },
        { label: 'Start Time (London)', value: timeCell('commodityReportStartTime', appConfig.commodityReportStartTime) }
    ];

    const iceDownloadRows = [
        { label: 'Start Time (London)', value: timeCell('iceDownloadStartTime', appConfig.iceDownloadStartTime) }
    ];

    const archivingRows = [
        { label: 'Start Time (London)', value: timeCell('archivingStartTime', appConfig.archivingStartTime) },
        { label: 'Day of Week',          value: dayCell('archivingDayOfWeek', appConfig.archivingDayOfWeek) }
    ];

    const activeJobs = appConfig.activeJobs ?? [];

    return (
        <div className={styles.wrapper}>
            {activeJobs.length > 0 && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px', padding: '10px 14px', margin: '16px 16px 0', color: '#856404' }}>
                    <strong>Warning:</strong> {activeJobs.join(' and ')} {activeJobs.length === 1 ? 'is' : 'are'} currently running.
                    Saving and restarting WebJobs now may interrupt the active process.
                </div>
            )}

            <div className={styles.row}>
                <div className={styles.panel}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <GridHeader title="General" />
                        {!editing && (
                            <button onClick={startEditing} className={styles.editBtn}>Edit</button>
                        )}
                    </div>
                    <Grid columns={appConfigColumns} defaultColumnSpacing={2} rows={generalRows} />
                </div>
                <div className={styles.panel}>
                    <GridHeader title="Daily Batch" />
                    <Grid columns={appConfigColumns} defaultColumnSpacing={2} rows={dailyBatchRows} />
                </div>
            </div>

            <div className={styles.row}>
                <div className={styles.panel}>
                    <GridHeader title="Commodity Report" />
                    <Grid columns={appConfigColumns} defaultColumnSpacing={2} rows={commodityRows} />
                </div>
                <div className={styles.panel}>
                    <GridHeader title="ICE Download" />
                    <Grid columns={appConfigColumns} defaultColumnSpacing={2} rows={iceDownloadRows} />
                </div>
            </div>

            <div className={styles.row}>
                <div className={`${styles.panel} ${styles.panelHalf}`}>
                    <GridHeader title="Archiving" />
                    <Grid columns={appConfigColumns} defaultColumnSpacing={2} rows={archivingRows} />
                </div>
            </div>

            {editing && (
                <div className={styles.actions}>
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges || appConfigSaving}
                    >
                        {appConfigSaving ? 'Saving...' : 'Save & Restart WebJobs'}
                    </button>
                    <button onClick={cancelEditing} disabled={appConfigSaving} style={{ marginLeft: '8px' }}>
                        Cancel
                    </button>
                    {successMessage && (
                        <span style={{ marginLeft: '12px', color: 'green' }}>{successMessage}</span>
                    )}
                    {appConfigSaveError && (
                        <div style={{ color: 'red', marginTop: '4px' }}>{appConfigSaveError}</div>
                    )}
                </div>
            )}
        </div>
    );
};

const mapDispatchToProps = { AppConfigConnected, saveAppConfig };

const mapStateToProps = state => ({
    appConfig: selectors.getAppConfig(state),
    appConfigSaving: selectors.getAppConfigSaving(state),
    appConfigSaveError: selectors.getAppConfigSaveError(state),
    appConfigSaved: selectors.getAppConfigSaved(state)
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(AppConfig));
